import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Logger } from 'src/utils/Logger';
import {
  acquireSharePointTokenInteractive,
  acquireSharePointTokenSilent,
} from 'src/data-capture/file-management/drop-zone/sharePointToken';

type SharepointFolderPickerResult = {
  driveId: string;
  folderId: string;
  folderName: string;
};

async function discoverSharepointBaseUrl(
  instance: ReturnType<typeof useMsal>['instance'],
  account: NonNullable<ReturnType<typeof useMsal>['accounts'][0]>,
): Promise<string> {
  const tokenResp = await instance.acquireTokenSilent({
    scopes: ['Files.Read.All'],
    account,
  });
  const resp = await fetch('https://graph.microsoft.com/v1.0/me/drive', {
    headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
  });
  if (!resp.ok) throw new Error(resp.statusText);
  const drive = await resp.json();
  const url = new URL(drive.webUrl);
  if (!url.hostname.endsWith('sharepoint.com')) {
    throw new Error(`Account is not a SharePoint work account (found: ${url.origin})`);
  }
  return url.origin;
}

async function acquireWriteToken(
  instance: ReturnType<typeof useMsal>['instance'],
  account: NonNullable<ReturnType<typeof useMsal>['accounts'][0]>,
): Promise<string> {
  const scopes = ['Files.ReadWrite.All'];
  try {
    const resp = await instance.acquireTokenSilent({ scopes, account });
    return resp.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Incremental consent: the user previously only granted Files.Read.All.
      // acquireTokenPopup triggers a consent dialog for the additional write scope.
      const resp = await instance.acquireTokenPopup({ scopes, account });
      return resp.accessToken;
    }
    throw err;
  }
}

function sanitiseSharepointFileName(fileName: string): string {
  // Graph's path addressing treats "/" and "\" as folder separators, so an
  // unsanitised name would silently create folders instead of a file. The other
  // characters are rejected outright by SharePoint. Trailing dots/spaces are also
  // invalid, so strip them too.
  const cleaned = fileName
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/, '');
  return cleaned || 'file';
}

function waitForFolderSelection(
  win: Window,
  getToken: (resource: string) => Promise<string>,
  channelId: string,
): Promise<SharepointFolderPickerResult | null> {
  return new Promise(resolve => {
    let cleanup: (() => void) | null = null;

    const doCleanup = () => {
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
    };

    const handleInit = (event: MessageEvent) => {
      if (
        event.source !== win ||
        event.data?.type !== 'initialize' ||
        event.data?.channelId !== channelId
      ) {
        return;
      }

      window.removeEventListener('message', handleInit);
      Logger.info('Folder picker MessagePort established');

      const port = event.ports[0];

      port.addEventListener('message', async (msg: MessageEvent) => {
        const { data } = msg;

        if (data.type === 'notification') {
          Logger.info(`Folder picker notification: ${JSON.stringify(data.data)}`);
          return;
        }

        if (data.type !== 'command') return;

        port.postMessage({ type: 'acknowledge', id: data.id });

        const command = data.data;
        Logger.info(`Folder picker command: ${command.command}`);

        switch (command.command) {
          case 'authenticate': {
            try {
              const token = await getToken(command.resource);
              port.postMessage({
                type: 'result',
                id: data.id,
                data: { result: 'token', token },
              });
            } catch (err) {
              // No popup fallback here: this handler is not a user gesture, so a popup would
              // be blocked. Report the failure to the picker, which prompts the user to sign
              // in from within its own window.
              if (err instanceof InteractionRequiredAuthError) {
                Logger.warn('Folder picker token needs interaction', { error: String(err) });
              } else {
                Logger.error('Folder picker auth failed', err);
              }
              port.postMessage({
                type: 'result',
                id: data.id,
                data: {
                  result: 'error',
                  error: { code: 'authError', message: String(err) },
                },
              });
            }
            break;
          }
          case 'pick': {
            const items: unknown[] = command.items || [];
            port.postMessage({
              type: 'result',
              id: data.id,
              data: { result: 'success' },
            });

            if (items.length > 0) {
              const item = items[0] as Record<string, unknown>;
              const parentReference = item.parentReference as Record<string, unknown> | undefined;
              const driveId = (parentReference?.driveId as string | undefined) ?? '';
              const folderId = (item.id as string | undefined) ?? '';
              const folderName = (item.name as string | undefined) ?? '';

              Logger.info(
                `Folder selected: ${folderName} (driveId=${driveId}, folderId=${folderId})`,
              );
              doCleanup();
              win.close();
              resolve({ driveId, folderId, folderName });
            } else {
              doCleanup();
              win.close();
              resolve(null);
            }
            break;
          }
          case 'close': {
            Logger.info('Folder picker closed by user');
            doCleanup();
            win.close();
            resolve(null);
            break;
          }
          default: {
            port.postMessage({
              type: 'result',
              id: data.id,
              data: {
                result: 'error',
                error: { code: 'unsupportedCommand', message: command.command },
                isExpected: true,
              },
            });
            break;
          }
        }
      });

      port.start();
      port.postMessage({ type: 'activate' });
    };

    window.addEventListener('message', handleInit);

    const checkClosed = setInterval(() => {
      if (win.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleInit);
        cleanup = null;
        resolve(null);
      }
    }, 1000);

    cleanup = () => {
      clearInterval(checkClosed);
      window.removeEventListener('message', handleInit);
      if (!win.closed) {
        win.close();
      }
    };
  });
}

export function useSharepointFolderPicker() {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;
  const isConnected = !!account;

  // A full-page redirect is used (not loginPopup) because the popup's redirect URI
  // is our app origin, so a popup would load the whole SPA instead of closing.
  // MSAL returns the user to this page after sign-in via handleRedirectPromise.
  //
  // prompt: 'consent' forces the Microsoft consent screen. Without it, a user whose
  // SharePoint consent has lapsed (revoked in "My Apps") is silently signed straight
  // back in by their live session without re-granting, so the upload keeps failing and
  // the redirect looks like a bare page refresh.
  const connect = () =>
    instance.loginRedirect({
      scopes: ['User.Read', 'Files.ReadWrite.All'],
      prompt: 'consent',
    });

  const pickFolder = async (): Promise<SharepointFolderPickerResult | null> => {
    if (!account) {
      throw new Error('No Microsoft account connected. Please sign in to SharePoint first.');
    }

    const baseUrl = await discoverSharepointBaseUrl(instance, account);
    const authToken = await acquireSharePointTokenInteractive(instance, account, baseUrl);
    const channelId = String(Date.now());

    const pickerParams = {
      sdk: '8.0',
      entry: {
        oneDrive: { files: {} },
      },
      authentication: {},
      messaging: {
        origin: window.location.origin,
        channelId,
      },
      selection: {
        mode: 'single',
      },
      typesAndSources: {
        mode: 'folders',
        pivots: {
          oneDrive: true,
          recent: false,
        },
      },
      commands: {
        pick: {
          action: 'select',
        },
      },
    };

    const queryString = new URLSearchParams({
      filePicker: JSON.stringify(pickerParams),
    });
    const pickerUrl = `${baseUrl}/_layouts/15/FilePicker.aspx?${queryString}`;

    const win = window.open('', 'SharePointFolderPicker', 'width=1080,height=680');
    if (!win) {
      throw new Error('Popup was blocked. Please allow popups for this site.');
    }

    const form = win.document.createElement('form');
    form.setAttribute('action', pickerUrl);
    form.setAttribute('method', 'POST');
    win.document.body.append(form);

    const input = win.document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', 'access_token');
    input.setAttribute('value', authToken);
    form.appendChild(input);

    form.submit();
    Logger.info('Folder picker form submitted');

    const getToken = (resource: string) =>
      acquireSharePointTokenSilent(instance, account, resource);
    return waitForFolderSelection(win, getToken, channelId);
  };

  // Acquiring the write scope may trigger an interactive consent popup, so it must be
  // called once up front rather than per file — otherwise cancelling consent would
  // re-prompt for every file in a batch.
  const acquireWriteAccess = async (): Promise<string> => {
    if (!account) {
      throw new Error('No Microsoft account connected.');
    }
    return acquireWriteToken(instance, account);
  };

  const createUploadSession = async (
    driveId: string,
    folderId: string,
    fileName: string,
    writeToken: string,
  ): Promise<string> => {
    const safeFileName = sanitiseSharepointFileName(fileName);
    const encodedFileName = encodeURIComponent(safeFileName);
    const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${folderId}:/${encodedFileName}:/createUploadSession`;

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${writeToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        item: {
          '@microsoft.graph.conflictBehavior': 'rename',
          name: safeFileName,
        },
      }),
    });

    if (!resp.ok) {
      const errorText = await resp.text();
      throw new Error(`createUploadSession failed (${resp.status}): ${errorText}`);
    }

    const data = await resp.json();
    return data.uploadUrl as string;
  };

  return { pickFolder, createUploadSession, acquireWriteAccess, isConnected, connect };
}
